import { Card, Text, View } from "@src/components";
import type { WasteSenseSummaryStats } from "../summary";

interface WeeklySummaryProps {
  stats: WasteSenseSummaryStats;
}

interface KpiCardProps {
  label: string;
  value: string;
  helper?: string;
}

function KpiCard({ label, value, helper }: KpiCardProps) {
  return (
    <Card flex={1} gap="xs" minWidth={160}>
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        {label}
      </Text>
      <Text textStyle="h2" color="text">
        {value}
      </Text>
      {helper ?
        <Text textStyle="xs/regular" color="textMuted">
          {helper}
        </Text>
      : null}
    </Card>
  );
}

/**
 * "Weekly summary" block — 4-up KPI grid backed by computed stats.
 *
 * Mirrors the static report KPIs:
 *   Camera Events Processed | Analysed by AI | Potential Events | Priority Incidents
 */
export function WeeklySummary({ stats }: WeeklySummaryProps) {
  const fmt = (n: number) => n.toLocaleString();
  const pct = (n: number) => `${n.toFixed(1)}%`;

  return (
    <View gap="md">
      <Text textStyle="h4" color="text">
        Weekly summary
      </Text>
      <View flexDirection="row" flexWrap="wrap" gap="md">
        <KpiCard label="Camera Events Processed" value={fmt(stats.eventsProcessed)} helper="Total received this week" />
        <KpiCard
          label="Analysed by AI"
          value={fmt(stats.aiReviewedEvents)}
          helper={`${pct(stats.analysedPct)} review completion`}
        />
        <KpiCard label="Potential Events" value={fmt(stats.potentialEvents)} helper="Flagged by AI for review" />
        <KpiCard
          label="Priority Incidents"
          value={fmt(stats.priorityIncidents)}
          helper="Retained after customer-safe review"
        />
      </View>
    </View>
  );
}
